
import React, { PropsWithChildren, useEffect, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';

import './MitekFlow.css';

import SelectDocument, { ISelectDocumentProps } from './SelectDocument/SelectDocument';

import DriversLicense, { IDriversLicenseProps } from './DriversLicense/DriversLicense';
import Passport, { IPassportProps } from './Passport/Passport';
import Results, { IMitekFlowResultProps } from './Results/Results';
import { useMitekContext, SupportedDocumentTypes } from '../../providers/MitekProvider/MitekProvider';
import Selfie, { ISelfieProps } from './Selfie/Selfie';
import useSpaTracking from '../../hooks/useSpaTracking';
import SessionStorage from '../../utils/SessionStorage';
import { AcquisitionsLoader } from '../../components/AcquisitionsLoader/AcquisitionsLoader';

type TMitekFlowStages = 'DriversLicense' | 'DriversLicenseBack' | 'Passport' | 'ResultsSuccess' | 'ResultsFailure' | 'Selfie' | 'SelectDocument' | '';

export interface IMitekFlowComponentProps {
    flowStage?: TMitekFlowStages;
    
    mainTitle?: string;
    subtitle?: string;
    imgName?: string;
    imgPath?: string;
    imgParam?: string;
    imgAltText?: string;
    description?: string;
    
    ctaButtonText?: string;
    setFlowStage:  THandlerFunction;
    setSpinning:  THandlerFunction;
}


interface IMitekFlowProps extends MappedComponentProperties {
    ctaButtonText?: string;
    flowStage?: TMitekFlowStages;

    usingHardReset?: boolean;
    usingSpaBackButton?: boolean;

    selectIdProperties?: ISelectDocumentProps

    driverFrontProperties?: IDriversLicenseProps;
    driverBackProperties?: IDriversLicenseProps;

    passportProperties?: IPassportProps;

    selfieProperties?: ISelfieProps;

    resultsSuccessProperties?: IMitekFlowResultProps;
    resultsFailureProperties?: IMitekFlowResultProps;
}

export const MitekFlowEditConfig = {
    emptyLabel: 'Mitek Flow',
    isEmpty: (props: PropsWithChildren<IMitekFlowProps>) => {
        return !props || !props?.ctaButtonText;
    },
};

const MitekFlow = (props: PropsWithChildren<IMitekFlowProps>): JSX.Element => {
    const { updateSpaPage, updateSpaTrackingId } = useSpaTracking();
    const [ isSpinning, setSpinning ] = useState(false);
    const isAuthor = props.isInEditor || window.location.search.includes('?wcmmode=disabled');
    const [flowStage, setFlowStage] = useState<TMitekFlowStages>(isAuthor && props.flowStage ? props.flowStage : 'SelectDocument');
    const { setMitekProps, MitekProps, clearResults } = useMitekContext();

    const updateMitekPropsDocumentType = (nextDocumentType : SupportedDocumentTypes) => {
        if (MitekProps.mitekDocumentType === nextDocumentType) {
            return;
        }

        setMitekProps(
            (previousProps) => {
                return {
                    ...previousProps,
                    mitekDocumentType: nextDocumentType,
                };
            },
        );
    };

    useEffect(() => {
        updateSpaTrackingId(SessionStorage.getObject('initResponse')?.SessionId || Math.random());

        if (props.usingSpaBackButton) {
            window.history.replaceState({}, '', window.location.href);
            
            const spaBackHandler: EventListener = (event: PopStateEvent) => {
                if (event.state?.stage && event.state.stage !== 'SelectDocument') {
                    event.preventDefault();
                    clearResults();
                    setFlowStage('SelectDocument');
                }
            };
            
            window.addEventListener('popstate', spaBackHandler, true);
            return () => {
                window.removeEventListener('popstate', spaBackHandler, true);
            };
        }
    }, []);

    useEffect(() => {
        if (isAuthor) {
            setFlowStage(props.flowStage ?? 'SelectDocument');
        }
    }, [props]);

    const getCurrentFlowStage = (): JSX.Element => {
        updateSpaPage(flowStage);
        if (props.usingSpaBackButton && flowStage !== 'SelectDocument') {
            window.history.pushState({ stage: flowStage }, '', window.location.href);
        }

        const commonProps = {
            ctaButtonText: props.ctaButtonText,
            setFlowStage,
            setSpinning,
        };
        switch (flowStage) {
            case 'DriversLicense':
                updateMitekPropsDocumentType('DL_FRONT');
                return <DriversLicense isFront={true} {...commonProps} {...props.driverFrontProperties} />;

            case 'DriversLicenseBack':
                updateMitekPropsDocumentType('PDF417_BARCODE');
                return <DriversLicense isFront={false} {...commonProps} {...props.driverBackProperties} />;

            case 'Passport':
                updateMitekPropsDocumentType('PASSPORT');
                return <Passport {...commonProps} {...props.passportProperties} />;

            case 'ResultsSuccess':
                return <Results isRetake={false} {...commonProps} {...props.resultsSuccessProperties} />;

            case 'ResultsFailure':
                return <Results isRetake={true} usingHardReset={props.usingHardReset} {...commonProps} {...props.resultsFailureProperties} />;
            
            case 'Selfie':
                updateMitekPropsDocumentType('SELFIE');
                return <Selfie {...commonProps} {...props.selfieProperties} />;

            default:
                return <SelectDocument {...commonProps} {...props.selectIdProperties} />;
        }
    };

    return (<>
        {isSpinning ? (<AcquisitionsLoader processingSteps={[]} />)
            :
            (<>
                {getCurrentFlowStage()}
            </>
            )
        }
    </>
    );
};

export default MitekFlow;
